/**
 * Phase 11: Attorney Review Queue Panel
 * Admin/attorney-only view showing all active reviews with enriched context.
 * Accessible from the admin dashboard as a dedicated tab.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE as API } from '../lib/apiBase';

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.detail?.message || data?.detail || 'Request failed';
    throw Object.assign(new Error(msg), { status: res.status, data });
  }
  return data;
};

const REVIEW_STATUS_COLORS = {
  active: 'bg-blue-100 text-blue-700 border-blue-200',
  in_review: 'bg-purple-100 text-purple-700 border-purple-200',
  completed: 'bg-green-100 text-green-700 border-green-200',
};

const REVIEW_STATUS_LABELS = {
  active: '📋 Awaiting Attorney',
  in_review: '🔍 Under Review',
  completed: '✅ Complete',
};

const STRENGTH_COLORS = {
  STRONG: 'text-green-700 bg-green-50 border-green-200',
  MODERATE: 'text-yellow-700 bg-yellow-50 border-yellow-200',
  NEEDS_ATTENTION: 'text-red-700 bg-red-50 border-red-200',
};

// ---------------------------------------------------------------------------
// Comment Thread Panel (inline, per review)
// ---------------------------------------------------------------------------
const CommentThread = ({ review, onClose }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [stepId, setStepId] = useState('');
  const [fieldId, setFieldId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchComments = useCallback(async () => {
    try {
      const data = await apiFetch(`/api/review/${review.pathway_id}/comments`);
      setComments(data.comments || []);
    } catch (err) {
      setError(err.message || 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [review.pathway_id]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !stepId.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await apiFetch(`/api/review/${review.pathway_id}/comment`, {
        method: 'POST',
        body: JSON.stringify({
          workflow_step_id: stepId.trim(),
          field_id: fieldId.trim() || null,
          content: newComment.trim(),
        }),
      });
      setNewComment('');
      setFieldId('');
      await fetchComments();
    } catch (err) {
      setError(err.message || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h3 className="font-bold text-gray-900 text-lg">
              💬 Comments — {review.pathway_id}
            </h3>
            <p className="text-sm text-gray-500">
              Tier: {review.review_tier_label} · User: {review.user_id?.substring(0, 8)}…
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Comments List */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {loading ? (
            <div className="text-center text-gray-400 py-6">
              <i className="fas fa-spinner fa-spin mr-2"></i> Loading comments…
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center text-gray-400 py-6">
              <i className="fas fa-comment-slash text-3xl mb-2 block"></i>
              No comments yet. Add the first comment below.
            </div>
          ) : (
            comments.map((c) => (
              <div
                key={c.id}
                className={`p-4 rounded-lg border ${c.orphaned ? 'border-amber-200 bg-amber-50' : 'border-purple-200 bg-purple-50'}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-purple-700 uppercase">
                    Step: {c.workflow_step_id}
                    {c.field_id && ` → ${c.field_id}`}
                    {c.step_name_at_creation && ` (${c.step_name_at_creation})`}
                  </span>
                  <span className="text-xs text-gray-400">
                    {c.timestamp ? new Date(c.timestamp).toLocaleString() : ''}
                    {c.edited && ' (edited)'}
                    {c.orphaned && (
                      <span className="ml-2 text-amber-600 font-semibold">⚠️ Orphaned</span>
                    )}
                  </span>
                </div>
                <p className="text-gray-800 text-sm">{c.content}</p>
              </div>
            ))
          )}
        </div>

        {/* Add Comment Form */}
        {review.status !== 'completed' && (
          <div className="p-5 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
            <form onSubmit={handleSubmitComment} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Step ID (required)"
                  value={stepId}
                  onChange={(e) => setStepId(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none"
                  required
                />
                <input
                  type="text"
                  placeholder="Field ID (optional)"
                  value={fieldId}
                  onChange={(e) => setFieldId(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none"
                />
              </div>
              <textarea
                placeholder="Enter your comment…"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none resize-none"
                required
              />
              {error && <p className="text-red-600 text-sm">{error}</p>}
              <button
                type="submit"
                disabled={submitting || !newComment.trim() || !stepId.trim()}
                className="w-full py-2 bg-purple-600 text-white rounded-lg font-semibold text-sm hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Adding…' : '+ Add Comment'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Complete Review Modal
// ---------------------------------------------------------------------------
const CompleteReviewModal = ({ review, onComplete, onClose }) => {
  const [feedback, setFeedback] = useState('');
  const [approved, setApproved] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!feedback.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const data = await apiFetch(`/api/review/${review.pathway_id}/complete`, {
        method: 'POST',
        body: JSON.stringify({ overall_feedback: feedback.trim(), approved }),
      });
      onComplete(data);
    } catch (err) {
      setError(err.message || 'Failed to complete review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h3 className="font-bold text-gray-900 text-lg">✅ Complete Review</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Overall Verdict
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={approved}
                  onChange={() => setApproved(true)}
                  className="accent-green-600"
                />
                <span className="text-sm font-medium text-green-700">✅ Looks Good</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={!approved}
                  onChange={() => setApproved(false)}
                  className="accent-amber-600"
                />
                <span className="text-sm font-medium text-amber-700">⚠️ Needs Revision</span>
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Overall Feedback <span className="text-red-500">*</span>
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={5}
              placeholder="Provide overall feedback to the applicant…"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-400 focus:outline-none resize-none"
              required
            />
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !feedback.trim()}
              className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {submitting ? 'Completing…' : 'Mark Complete & Generate Docs'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Review Card
// ---------------------------------------------------------------------------
const ReviewCard = ({ review, onRefresh }) => {
  const [showComments, setShowComments] = useState(false);
  const [showComplete, setShowComplete] = useState(false);

  const handleComplete = (data) => {
    setShowComplete(false);
    onRefresh();
  };

  const evidenceSummary = review.evidence_strategy_summary;

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4 hover:shadow-md transition-shadow">
        {/* Header row */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${REVIEW_STATUS_COLORS[review.status] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                {REVIEW_STATUS_LABELS[review.status] || review.status}
              </span>
              <span className="text-xs text-gray-500 font-mono">
                {review.review_tier_label}
              </span>
            </div>
            <h4 className="font-bold text-gray-900 text-base">
              {review.pathway_id}
            </h4>
            <p className="text-xs text-gray-500 mt-0.5">
              User: <span className="font-mono">{review.user_id}</span>
            </p>
          </div>
          <div className="text-right text-xs text-gray-400 space-y-0.5">
            {review.purchase_timestamp && (
              <div>Purchased: {new Date(review.purchase_timestamp).toLocaleDateString()}</div>
            )}
            {review.attorney_id && (
              <div className="text-purple-600 font-medium">Attorney assigned ✓</div>
            )}
          </div>
        </div>

        {/* Case Strength + Evidence */}
        <div className="grid grid-cols-2 gap-3">
          {review.case_strength_score != null && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <p className="text-xs text-gray-500 mb-1 font-medium uppercase">Case Strength</p>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-gray-900">
                  {Math.round(review.case_strength_score)}
                </span>
                <span className="text-xs text-gray-400">/ 100</span>
              </div>
            </div>
          )}
          {evidenceSummary && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <p className="text-xs text-gray-500 mb-1 font-medium uppercase">Evidence</p>
              <div className={`text-sm font-bold px-2 py-0.5 rounded-full inline-block border ${STRENGTH_COLORS[evidenceSummary.overall_strength] || 'text-gray-600 bg-gray-50 border-gray-200'}`}>
                {evidenceSummary.overall_strength}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {evidenceSummary.strong_count}✅ {evidenceSummary.needs_work_count}⚠️ {evidenceSummary.insufficient_count}❌
              </div>
            </div>
          )}
        </div>

        {/* Applicant Notes */}
        {review.applicant_notes && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-blue-700 mb-1">Applicant Notes</p>
            <p className="text-sm text-gray-800">{review.applicant_notes}</p>
          </div>
        )}

        {/* Evidence Recommendations */}
        {evidenceSummary?.recommendations?.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-amber-700 mb-2">Evidence Gaps</p>
            <ul className="space-y-1">
              {evidenceSummary.recommendations.slice(0, 3).map((rec, i) => (
                <li key={i} className="text-xs text-gray-700 flex items-start gap-1">
                  <span className="text-amber-500 mt-0.5">•</span>
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Footer actions */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <button
            onClick={() => setShowComments(true)}
            className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-800 font-medium transition-colors"
          >
            <i className="fas fa-comments"></i>
            Comments ({review.comments_count || 0})
          </button>

          {review.status !== 'completed' ? (
            <button
              onClick={() => setShowComplete(true)}
              className="flex items-center gap-2 px-4 py-1.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors"
            >
              <i className="fas fa-check"></i>
              Mark Complete
            </button>
          ) : (
            <div className="flex items-center gap-2 text-sm text-green-600 font-medium">
              <i className="fas fa-check-circle"></i>
              {review.second_doc_generated ? 'Docs Regenerated' : 'Completed'}
            </div>
          )}
        </div>
      </div>

      {showComments && (
        <CommentThread review={review} onClose={() => setShowComments(false)} />
      )}
      {showComplete && (
        <CompleteReviewModal
          review={review}
          onComplete={handleComplete}
          onClose={() => setShowComplete(false)}
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Main Panel
// ---------------------------------------------------------------------------
const AttorneyReviewQueuePanel = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch('/api/review/queue');
      setReviews(data.reviews || []);
    } catch (err) {
      setError(err.message || 'Failed to load review queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400">
        <i className="fas fa-spinner fa-spin mr-2"></i> Loading review queue…
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-center">
        <i className="fas fa-exclamation-triangle text-red-400 text-3xl mb-2 block"></i>
        <p className="text-red-700 font-medium">{error}</p>
        <button
          onClick={fetchQueue}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-gray-900">
            <i className="fas fa-gavel mr-2 text-purple-600"></i>
            Attorney Review Queue
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {reviews.length} active {reviews.length === 1 ? 'review' : 'reviews'} awaiting action
          </p>
        </div>
        <button
          onClick={fetchQueue}
          className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors"
        >
          <i className="fas fa-sync-alt"></i> Refresh
        </button>
      </div>

      {/* Queue */}
      {reviews.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
          <i className="fas fa-check-circle text-green-400 text-4xl mb-3 block"></i>
          <p className="text-gray-600 font-medium">All clear — no pending reviews</p>
          <p className="text-gray-400 text-sm mt-1">
            New reviews will appear here when users request attorney review.
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} onRefresh={fetchQueue} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AttorneyReviewQueuePanel;
